import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

type InboxEvent =
  | { type: "conversation"; conversationId: string }
  | { type: "message"; conversationId: string }
  | { type: "channel"; channelId: string }
  | { type: "contacts" }
  | { type: "board" };

/**
 * Keeps the inbox live.
 *
 * Two things matter here, and both are lessons from the reference system rather than inventions.
 *
 * **Coalescing.** Events arrive in bursts — a phone syncing, a history batch, three people replying
 * at once — and invalidating per event meant the reference app refetched everything 15 times in a
 * second. They settled on one refresh per ~1.5s; so do we.
 *
 * **Events say "something changed", never what.** The payload carries ids, not data, so the refetch
 * goes back through the ordinary tenant-scoped routes. A stream that carried message bodies would be
 * a second way to read data, needing its own authorisation story.
 *
 * `EventSource` reconnects on its own, and the server sends a `retry:` hint. The `refetchOnWindowFocus`
 * default stays on underneath as the backstop for a stream that is down.
 */
const COALESCE_MS = 1500;

export function useInboxStream(): void {
  const qc = useQueryClient();
  // Held in a ref so a burst mutates one pending set rather than re-rendering per event.
  const pending = useRef({
    conversations: new Set<string>(), list: false, channels: false, contacts: false, board: false,
  });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const flush = () => {
      timer.current = null;
      const { conversations, list, channels, contacts, board } = pending.current;
      pending.current = {
        conversations: new Set(), list: false, channels: false, contacts: false, board: false,
      };

      // One open thread is the common case; invalidate just it rather than every cached thread.
      for (const id of conversations) {
        void qc.invalidateQueries({ queryKey: ["conversation", id] });
      }
      if (list) {
        void qc.invalidateQueries({ queryKey: ["conversations"] });
        void qc.invalidateQueries({ queryKey: ["queue"] });
        void qc.invalidateQueries({ queryKey: ["today"] });
      }
      if (channels) void qc.invalidateQueries({ queryKey: ["channels"] });
      /*
       * A card moved, or a time was booked. Both quadros read `["board", key]`, the ficha reads
       * `["person"]`, the care tick also changes a patient's status, and a booking from the
       * public link lands in the agenda — so all of those refetch. Broad on purpose: the event
       * carries no id, because every one of these is a list.
       */
      if (board) {
        for (const key of ["board", "person", "today", "appointments", "patients", "patient", "dashboard"]) {
          void qc.invalidateQueries({ queryKey: [key] });
        }
      }
      // A rename touches every surface that prints a name, and an address-book sync renames in bulk —
      // so this refetches broadly rather than trying to name the affected rows.
      if (contacts) {
        void qc.invalidateQueries({ queryKey: ["conversations"] });
        void qc.invalidateQueries({ queryKey: ["contacts"] });
        for (const key of ["conversation", "queue", "today"]) {
          void qc.invalidateQueries({ queryKey: [key] });
        }
      }
    };

    const schedule = () => {
      if (timer.current) return;
      timer.current = setTimeout(flush, COALESCE_MS);
    };

    let source: EventSource | null = null;
    let reopen: ReturnType<typeof setTimeout> | null = null;
    let backoff = 1_000;
    let lastEvent = Date.now();
    let closed = false;

    const connect = () => {
      if (closed) return;
      source = new EventSource("/api/events", { withCredentials: true });

      source.onopen = () => { backoff = 1_000; lastEvent = Date.now(); };

      // The server's heartbeat. A named event, so it never reaches `onmessage` — it exists only to
      // prove the stream is still delivering, which a quiet inbox otherwise cannot.
      source.addEventListener("ping", () => { lastEvent = Date.now(); });

      source.onmessage = (e) => {
        lastEvent = Date.now();
        let event: InboxEvent;
        try {
          event = JSON.parse(e.data) as InboxEvent;
        } catch {
          return; // a malformed frame is not worth tearing the stream down for
        }
        if (event.type === "channel") pending.current.channels = true;
        else if (event.type === "contacts") pending.current.contacts = true;
        // Explicit, not a fallthrough: the `else` below reads `conversationId`, and an event
        // without one would quietly add `undefined` to the set and invalidate nothing useful.
        else if (event.type === "board") pending.current.board = true;
        else {
          pending.current.conversations.add(event.conversationId);
          // Any message or status change can reorder the list and move unread counts.
          pending.current.list = true;
        }
        schedule();
      };

      source.onerror = () => {
        // `EventSource` retries a dropped connection by itself, but **not** a failed one: per spec a
        // non-200 status or the wrong content-type closes it permanently. An nginx 502 during a
        // deploy is exactly that, and the server's own `retry: 3000` hint guarantees the retry lands
        // inside the upstream gap. Swallowing the error left every open tab rendering a healthy
        // inbox forever while messages persisted and never appeared — the precise failure this
        // stream was built to eliminate.
        if (source?.readyState !== EventSource.CLOSED || closed) return;
        source.close();
        reopen = setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, 30_000);
      };
    };

    connect();

    // Belt and braces: a connection can be open and silent — a proxy holding the response, a laptop
    // resuming with a dead socket the browser has not noticed. The server sends a keep-alive comment
    // every 25s, so nothing for 90s means the stream is not delivering regardless of its readyState.
    const watchdog = setInterval(() => {
      if (closed || Date.now() - lastEvent < 90_000) return;
      source?.close();
      lastEvent = Date.now();
      connect();
    }, 30_000);

    // A tab that was backgrounded through a deploy comes back to a dead stream and no error.
    const recheck = () => {
      if (document.visibilityState !== "visible") return;
      if (!source || source.readyState === EventSource.CLOSED) { lastEvent = Date.now(); connect(); }
    };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("online", recheck);

    return () => {
      closed = true;
      source?.close();
      clearInterval(watchdog);
      if (reopen) clearTimeout(reopen);
      document.removeEventListener("visibilitychange", recheck);
      window.removeEventListener("online", recheck);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [qc]);
}
